/*eslint no-debugger: 0*/
import {
	Color,
	Scene,
	WebGLRenderer,
	Group,
	PerspectiveCamera,
	TextureLoader,
	Texture,
	Mesh,
	MeshBasicMaterial,
	PointsMaterial,
	SphereGeometry,
	Geometry,
	Vector3,
	Points,
} from 'three';
import { OrbitControls } from '../threejs/BetterOrbitControls';

export default function globe(mainScroll) {
	/* VARIABLES */

	let canvas;
	let scene;
	let renderer;
	let data;
	let globeElement;

	function isMobileDevice() {
		return (
			typeof window.orientation !== 'undefined' ||
			navigator.userAgent.indexOf('IEMobile') !== -1
		);
	}

	// Cache DOM selectors
	const container = document.getElementById('globe-container');

	// Object for country HTML elements and variables
	const elements = {};

	// Three group objects
	const groups = {
		main: null, // A group containing everything
		globe: null, // A group containing the globe sphere (and globe dots)
		globeDots: {}, // A group containing the globe dots
	};

	// Map properties for creation and rendering
	const props = {
		mapSize: {
			// Size of the map from the intial source image (on which the dots are positioned on)
			width: 2048 / 2,
			height: 1024 / 2,
		},
		globeRadius: 200, // Radius of the globe (used for many calculations)
		dotsAmount: 20, // Amount of dots to generate and animate randomly across the lines
		startingCountry: 'angola', // The key of the country to rotate the camera to during the introduction animation (and which country to start the cycle at)
		colours: {
			// Cache the colours
			globeDots: '#005288', // No need to use the Three constructor as this value is used for the HTML canvas drawing 'fillStyle' property
			lineDots: new Color('#18FFFF'),
		},
		alphas: {
			// Transparent values of materials
			globe: 0.4,
		},
	};

	// Angles used for animating the camera
	const camera = {
		object: null, // Three object of the camera
		controls: null, // Three object of the orbital controls
		angles: {
			// Object of the camera angles for animating
			current: {
				azimuthal: null,
				polar: null,
			},
			target: {
				azimuthal: null,
				polar: null,
			},
		},
	};

	// Booleans and values for animations
	const animations = {
		finishedIntro: false, // Boolean of when the intro animations have finished
		dots: {
			current: 0, // Animation frames of the globe dots introduction animation
			total: 170, // Total frames (duration) of the globe dots introduction animation,
			points: [], // Array to clone the globe dots coordinates to
		},
		globe: {
			current: 0, // Animation frames of the globe introduction animation
			total: 80, // Total frames (duration) of the globe introduction animation,
		},
		countries: {
			active: false, // Boolean if the country elements have been added and made active
			animating: false, // Boolean if the countries are currently being animated
			current: 0, // Animation frames of country elements introduction animation
			total: 120, // Total frames (duration) of the country elements introduction animation
			selected: null, // Three group object of the currently selected country
			index: null, // Index of the country in the data array
			timeout: null, // Timeout object for cycling to the next country
			initialDuration: 5000, // Initial timeout duration before starting the country cycle
			duration: 2000, // Timeout duration between cycling to the next country
		},
	};

	// Boolean to enable or disable rendering when window is in or out of focus
	let isHidden = false;

	/* SETUP */

	const getData = async () => {
		try {
			const results = await fetch(
				'wp-content/themes/sodecia/resources/assets/scripts/points.json',
			);
			data = await results.json();

			return setupScene();
		} catch (error) {
			console.error(error);
		}
	};

	const setupScene = () => {
		canvas = container.getElementsByTagName('canvas')[0];
		scene = new Scene();
		renderer = new WebGLRenderer({
			canvas,
			antialias: true,
			alpha: true,
			shadowMapEnabled: false,
		});
		renderer.setSize(canvas.clientWidth, canvas.clientHeight);
		renderer.setPixelRatio(1);
		//renderer.setClearColor(0x000000, 0);
		renderer.setClearColor(0xffffff, 0);

		// Main group that contains everything
		groups.main = new Group();
		groups.main.name = 'Main';

		// Group that contains dynamically created dots
		groups.lineDots = new Group();
		groups.lineDots.name = 'Dots';
		groups.main.add(groups.lineDots);

		// Add the main group to the scene
		scene.add(groups.main);

		// Render camera and add orbital controls
		addCamera();
		addControls();

		// Render objects
		addGlobe();
		addUserPoint();

		// Start the requestAnimationFrame loop
		render();

		if (isMobileDevice()) {
			animate();
		} else {
			mainScroll.on('call', value => {
				if (value === 'init-globe') {
					animate();
				}
			});
		}

		const canvasResizeBehaviour = () => {
			container.width = 571;
			container.height = 592;
			container.style.width = '571px';
			container.style.height = '592px';

			camera.object.aspect =
				container.offsetWidth / container.offsetHeight;
			camera.object.updateProjectionMatrix();
			renderer.setSize(container.offsetWidth, container.offsetHeight);
		};

		window.addEventListener('resize', canvasResizeBehaviour);
		window.addEventListener('orientationchange', canvasResizeBehaviour);
		canvasResizeBehaviour();
	};

	/* CAMERA AND CONTROLS */

	const addCamera = () => {
		const { clientWidth, clientHeight } = canvas;
		camera.object = new PerspectiveCamera(
			60,
			clientWidth / clientHeight,
			1,
			10000,
		);
		camera.object.position.z = props.globeRadius * 2.2;
	};

	const addControls = () => {
		camera.controls = new OrbitControls(camera.object, canvas);
		camera.controls.enableKeys = false;
		camera.controls.enablePan = false;
		camera.controls.enableZoom = false;
		camera.controls.enableDamping = false;
		camera.controls.enableRotate = false;

		// Set the initial camera angles to something crazy for the introduction animation
		camera.angles.current.azimuthal = -Math.PI;
		camera.angles.current.polar = 0;
	};

	/* RENDERING */

	const render = () => renderer.render(scene, camera.object);

	const animate = () => {
		if (!isHidden) {
			requestAnimationFrame(animate);
		}

		if (groups.globeDots) {
			introAnimate();
		}

		if (animations.finishedIntro) {
			animateDots();
		}

		positionElements();
		camera.controls.update();
		groups.globe.rotation.y += 0.001;
		render();
	};

	/* GLOBE */

	const addGlobe = () => {
		const textureLoader = new TextureLoader();
		textureLoader.setCrossOrigin(true);

		const radius = props.globeRadius - props.globeRadius * 0.02;
		const segments = 64;
		const rings = 64;

		// Make gradient
		const canvasSize = 128;
		const textureCanvas = document.createElement('canvas');
		textureCanvas.width = canvasSize;
		textureCanvas.height = canvasSize;
		const canvasContext = textureCanvas.getContext('2d');
		canvasContext.rect(0, 0, canvasSize, canvasSize);
		const canvasGradient = canvasContext.createLinearGradient(
			0,
			0,
			0,
			canvasSize,
		);
		canvasGradient.addColorStop(0, '#FFFFFF');
		canvasGradient.addColorStop(0.5, '#FFFFFF');
		canvasGradient.addColorStop(1, '#FFFFFF');
		canvasContext.fillStyle = canvasGradient;
		canvasContext.fill();

		// Make texture
		const texture = new Texture(textureCanvas);
		texture.needsUpdate = true;

		const geometry = new SphereGeometry(radius, segments, rings);
		const material = new MeshBasicMaterial({
			map: texture,
			transparent: true,
			opacity: 0,
		});

		globeElement = new Mesh(geometry, material);

		groups.globe = new Group();
		groups.globe.name = 'Globe';
		groups.globe.add(globeElement);
		groups.main.add(groups.globe);

		addGlobeDots();
	};

	const addGlobeDots = () => {
		const geometry = new Geometry();

		// Make circle
		const canvasSize = 16;
		const halfSize = canvasSize / 2;
		const textureCanvas = document.createElement('canvas');
		textureCanvas.width = canvasSize;
		textureCanvas.height = canvasSize;
		const canvasContext = textureCanvas.getContext('2d');
		canvasContext.beginPath();
		canvasContext.arc(halfSize, halfSize, halfSize, 0, 2 * Math.PI);
		canvasContext.fillStyle = props.colours.globeDots;
		canvasContext.fill();

		// Make texture
		const texture = new Texture(textureCanvas);
		texture.needsUpdate = true;

		const material = new PointsMaterial({
			map: texture,
			size: props.globeRadius / 120,
		});

		const addDot = function({ x, y }) {
			// Add a point with zero coordinates
			const point = new Vector3(0, 0, 0);
			geometry.vertices.push(point);

			// Add the coordinates to a new array for the intro animation
			const result = returnSphericalCoordinates(x, y);
			animations.dots.points.push(
				new Vector3(result.x, result.y, result.z),
			);
		};

		for (let i = 0; i < data.points.length; i++) {
			addDot(data.points[i]);
		}

		for (let country in data.countries) {
			addDot(data.countries[country].x, data.countries[country].y);
		}

		// debugger;

		// Add the points to the scene
		groups.globeDots = new Points(geometry, material);
		groups.globe.add(groups.globeDots);
	};

	const users = [
		{
			id: 0,
			name: 'John Yang',
			geo: {
				lat: 31.2304,
				lng: 121.4737,
				name: 'Shanghai, CN',
			},
			date: '01.23.2018',
		},
		{
			id: 1,
			name: 'Emma S.',
			geo: {
				lat: 55.6761,
				lng: 12.5683,
				name: 'Denmark, CPH',
			},
			date: '09.20.2018',
		},
		{
			id: 2,
			name: 'Spencer S.',
			geo: {
				lat: 34.0522,
				lng: -118.2437,
				name: 'Los Angeles, CA',
			},
			date: '12.25.2018',
		},
	];

	function convertLatLngToSphereCoords(latitude, longitude) {
		const phi = (latitude * Math.PI) / 180;
		const theta = ((longitude - 180) * Math.PI) / 180;
		const x = -(props.globeRadius + -1) * Math.cos(phi) * Math.cos(theta);
		const y = (props.globeRadius + -1) * Math.sin(phi);
		const z = (props.globeRadius + -1) * Math.cos(phi) * Math.sin(theta);
		return new Vector3(x, y, z);
	}

	const addUserPoint = function() {
		// This will create all of the user points onto the globe. But we are not using it right now because
		// we want to render only one point at a time.
		const mergedGeometry = new Geometry();
		const pingGeometry = new SphereGeometry(2, 2, 2);
		// The material that our ping will be created from.
		const material = new MeshBasicMaterial({ color: 0x0f881a });
		for (let user of users) {
			const pos = convertLatLngToSphereCoords(user.geo.lat, user.geo.lng);
			pingGeometry.translate(pos.x, pos.y, pos.z);
			mergedGeometry.merge(pingGeometry);
			pingGeometry.translate(-pos.x, -pos.y, -pos.z);
		}
		const total = new Mesh(mergedGeometry, material);
		groups.userPoints = total;
		groups.userPoints.name = 'User Points';
		scene.add(groups.userPoints);
	};

	/* COUNTRY LINES AND DOTS */

	const animateDots = () => {
		// Loop through the dots children group
		for (let i = 0; i < groups.lineDots.children.length; i++) {
			const dot = groups.lineDots.children[i];

			if (!dot._path) {
				// Create a random seed as a pseudo-delay
				if (Math.random() > 0.99) {
					dot._pathIndex = 0;
				}
			} else if (dot._path && dot._pathIndex < dot._path.length - 1) {
				// Show the dot
				if (!dot.visible) {
					dot.visible = true;
				}

				// Move the dot along the path vertice coordinates
				dot.position.x = dot._path[dot._pathIndex].x;
				dot.position.y = dot._path[dot._pathIndex].y;
				dot.position.z = dot._path[dot._pathIndex].z;

				// Advance the path index by 1
				dot._pathIndex++;
			} else {
				// Hide the dot and remove the path assingment
				dot.visible = false;
				dot._path = null;
			}
		}
	};

	/* ELEMENTS */

	let list = document.querySelector('.js-list');

	const positionElements = () => {
		const widthHalf = canvas.clientWidth / 2;
		const heightHalf = canvas.clientHeight / 2;

		// Loop through the elements array and reposition the elements
		for (const key in elements) {
			const { position, element } = elements[key];
			const { x, y } = getProjectedPosition(
				widthHalf,
				heightHalf,
				position,
			);

			// Construct the 3D translate string
			const elementStyle = element.style;
			const styleString = `translate3D(${x}px, ${y}px, 0)`;
			elementStyle.webkitTransform = styleString;
			elementStyle.WebkitTransform = styleString; // For Safari
			elementStyle.mozTransform = styleString;
			elementStyle.msTransform = styleString;
			elementStyle.oTransform = styleString;
			elementStyle.transform = styleString;
		}
	};

	/* INTRO ANIMATIONS */

	// Easing reference: https://gist.github.com/gre/1650294

	const easeInOutCubic = t =>
		t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
	const easeOutCubic = t => --t * t * t + 1;

	const introAnimate = () => {
		const { dots, globe, countries } = animations;

		if (dots.current <= dots.total) {
			const points = groups.globeDots.geometry.vertices;
			const totalLength = points.length;

			for (let i = 0; i < totalLength; i++) {
				// Get ease value and add delay based on loop iteration
				let dotProgress = easeInOutCubic(dots.current / dots.total);
				dotProgress = dotProgress + dotProgress * (i / totalLength);

				if (dotProgress > 1) {
					dotProgress = 1;
				}

				// Move the point
				points[i].x = dots.points[i].x * dotProgress;
				points[i].y = dots.points[i].y * dotProgress;
				points[i].z = dots.points[i].z * dotProgress;

				// Animate the camera at the same rate as the first dot
				if (i === 0) {
					const { current } = camera.angles;

					const azimuthalDifference =
						(current.azimuthal - 4.4) * dotProgress;
					camera.controls.setAzimuthalAngle(azimuthalDifference);

					// const polarDifference = (current.polar - target.polar) * dotProgress;
					camera.controls.setPolarAngle(0.95);
				}
			}

			dots.current++;

			// Update verticies
			groups.globeDots.geometry.verticesNeedUpdate = true;
		}

		if (dots.current >= dots.total * 0.65 && globe.current <= globe.total) {
			const globeProgress = easeOutCubic(globe.current / globe.total);
			globeElement.material.opacity = props.alphas.globe * globeProgress;
			globe.current++;
		}

		if (dots.current >= dots.total * 0.7 && !countries.active) {
			list.classList.add('active');
			countries.active = true;
		}
	};

	/* COORDINATE CALCULATIONS */

	// Returns an object of 3D spherical coordinates
	const returnSphericalCoordinates = (latitude, longitude) => {
		/*
            This function will take a latitude and longitude and calcualte the
            projected 3D coordiantes using Mercator projection relative to the
            radius of the globe.

            Reference: https://stackoverflow.com/a/12734509
        */

		// Convert latitude and longitude on the 90/180 degree axis
		latitude =
			((latitude - props.mapSize.width) / props.mapSize.width) * -180;
		longitude =
			((longitude - props.mapSize.height) / props.mapSize.height) * -90;

		// Calculate the projected starting point
		const radius =
			Math.cos((longitude / 180) * Math.PI) * props.globeRadius;
		const x = Math.cos((latitude / 180) * Math.PI) * radius;
		const y = Math.sin((longitude / 180) * Math.PI) * props.globeRadius;
		const z = Math.sin((latitude / 180) * Math.PI) * radius;

		return { x, y, z };
	};

	// Returns an object of 2D coordinates for projected 3D position
	const getProjectedPosition = (width, height, position) => {
		/*
            Using the coordinates of a country in the 3D space, this function will
            return the 2D coordinates using the camera projection method.
        */

		position = position.clone();
		const { x, y } = position.project(camera.object);

		return {
			x: x * width + width,
			y: -(y * height) + height,
		};
	};

	// Returns an object of the azimuthal and polar angles of a given map latitude and longitude
	// const returnCameraAngles = (latitude, longitude) => {
	//     /*
	//         This function will convert given latitude and longitude coordinates that are
	//         proportional to the map dimensions into values relative to PI (which the
	//         camera uses as angles).

	//         Note that the azimuthal angle ranges from 0 to PI, whereas the polar angle
	//         ranges from -PI (negative PI) to PI (positive PI).

	//         A small offset is added to the azimuthal angle as angling the camera directly on top of a point makes the lines appear flat.
	//     */

	//     let azimuthal = ((latitude - props.mapSize.width) / props.mapSize.width) * Math.PI;
	//     azimuthal = azimuthal + (Math.PI / 2);
	//     azimuthal = azimuthal + 0.1; // Add a small offset

	//     let polar = (longitude / (props.mapSize.height * 2)) * Math.PI;

	//     return { azimuthal, polar };
	// };

	/* INITIALISATION */

	if (!window.WebGLRenderingContext) {
		alert('WebGL not supported, please use a browser that supports WebGL');
	} else {
		getData();
	}
}



// WEBPACK FOOTER //
// ./scripts/components/globe-new.js